import React, { useState, useEffect } from 'react';
import { Building2, User, Award, Package, Users, DollarSign, Calendar, ChevronDown, ChevronUp, UserPlus, Globe } from 'lucide-react';
import { InputField, SelectField } from '../../../components/common';
import InfoTooltip from '../../../components/common/InfoTooltip';
import {
  industryOptions,
  regionOptions,
  countryOptions,
  currencyOptions,
  dealTypeOptions,
  quoteValidityOptions
} from '../../../config';
import { isValidOpportunityId } from '../../../config/validation';

/**
 * Deal Info Step - Customer & deal information
 * 
 * ⭐ E13: Added currency selection
 * ⭐ E12: Added quote validity period
 * ⭐ E16: Added autoComplete attributes
 * ⭐ E17: Expanded region/industry options
 * ⭐ M18: Added multiple contact types (Primary, Billing, Influencer)
 */
const DealInfoStep = ({ quoteData, setQuoteData, user }) => {
  // ⭐ M18: Track which contact sections are expanded
  const [showBillingContact, setShowBillingContact] = useState(
    !!(quoteData.billingContactName || quoteData.billingContactEmail)
  );
  const [showInfluencerContact, setShowInfluencerContact] = useState(
    !!(quoteData.influencerName || quoteData.influencerEmail)
  );

  // ⭐ Auto-fill sales rep info from logged-in user for new quotes
  useEffect(() => {
    if (user && !quoteData.salesRep && !quoteData.salesRepEmail) {
      setQuoteData(prev => ({
        ...prev,
        salesRep: user.name || '',
        salesRepEmail: user.email || ''
      }));
    }
  }, [user, quoteData.salesRep, quoteData.salesRepEmail, setQuoteData]);

  // ⭐ E13: Auto-set currency based on region
  const handleRegionChange = (e) => {
    const region = e.target.value;
    const regionCurrencyMap = {
      'US': 'USD',
      'UK': 'GBP',
      'EMEA': 'EUR',
      'MENA': 'AED',
      'ANZ': 'AUD',
      'APAC': 'USD',
      'LATAM': 'USD'
    };

    // Reset country when region changes
    setQuoteData({
      ...quoteData,
      region,
      country: '',
      currency: regionCurrencyMap[region] || 'USD'
    });
  };

  // Get available countries for the selected region
  const availableCountries = countryOptions[quoteData.region] || [];

  return (
    <div>
      <h2 className="text-2xl font-bold text-slate-800 mb-2">Deal Information</h2>
      <p className="text-slate-600 mb-8">Tell us about your customer and opportunity</p>

      <div className="space-y-8">
        {/* Salesforce Opportunity - First for CRM linkage */}
        <div>
          <h3 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
            <Package size={18} className="text-orange-500" />
            Salesforce Opportunity
            <span className="text-xs font-normal text-red-500 bg-red-50 px-2 py-0.5 rounded-full">Required</span>
          </h3>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Salesforce Opportunity ID <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={quoteData.opportunityId || ''}
              onChange={(e) => setQuoteData({...quoteData, opportunityId: e.target.value.trim()})}
              placeholder="006PE00000OHoJ5YAL"
              maxLength={18}
              className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono ${
                !quoteData.opportunityId
                  ? 'border-amber-300 bg-amber-50'
                  : isValidOpportunityId(quoteData.opportunityId)
                    ? 'border-green-300 bg-green-50'
                    : 'border-red-300 bg-red-50'
              }`}
            />
            {!quoteData.opportunityId ? (
              <p className="text-xs text-amber-600 mt-1">
                Required: Enter your 18-character Salesforce Opportunity ID (starts with 006)
              </p>
            ) : isValidOpportunityId(quoteData.opportunityId) ? (
              <p className="text-xs text-green-600 mt-1">
                ✓ Valid Salesforce Opportunity ID format
              </p>
            ) : (
              <p className="text-xs text-red-600 mt-1">
                ✗ Invalid format: Must be exactly 18 characters and start with "006"
                {quoteData.opportunityId.length !== 18 && ` (currently ${quoteData.opportunityId.length} chars)`}
              </p>
            )}
            <p className="text-xs text-slate-500 mt-1">
              Find the ID in your Salesforce Opportunity URL or Details page. Example: 006PE00000OHoJ5YAL
            </p>
          </div>
        </div>

        {/* Customer Information */}
        <div>
          <h3 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
            <Building2 size={18} className="text-blue-600" />
            Customer Information
          </h3>
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <InputField
                  label="Customer Name"
                  value={quoteData.customerName}
                  onChange={(e) => setQuoteData({...quoteData, customerName: e.target.value})}
                  placeholder="e.g., ICF International"
                  required
                />
                <p className="text-xs text-slate-500 mt-1">
                  Must match Salesforce Account Name exactly for reporting accuracy
                </p>
              </div>
              <InputField
                label="Customer Legal Name"
                value={quoteData.customerLegalName}
                onChange={(e) => setQuoteData({...quoteData, customerLegalName: e.target.value})}
                placeholder="e.g., ICF International Inc."
              />
            </div>
            {!quoteData.customerName && (
              <p className="text-red-600 text-sm -mt-4">Customer name is required</p>
            )}
            
            <InputField
              label="Address"
              value={quoteData.customerAddress}
              onChange={(e) => setQuoteData({...quoteData, customerAddress: e.target.value})}
              placeholder="9300 Lee Highway, San Francisco, CA 22031 USA"
            />
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Industry</label>
                <select
                  value={quoteData.industry}
                  onChange={(e) => setQuoteData({...quoteData, industry: e.target.value})}
                  className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Select Industry...</option>
                  {industryOptions.map(industry => (
                    <option key={industry} value={industry}>{industry}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2 flex items-center">
                  Region
                  <InfoTooltip
                    text="Region is the SplashBI sales territory (US, UK, EMEA, etc.) used for reporting and analytics. This auto-sets the currency."
                    position="top"
                  />
                </label>
                <select
                  value={quoteData.region}
                  onChange={handleRegionChange}
                  className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  {regionOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2 flex items-center gap-2">
                  <Globe size={14} className="text-blue-600" />
                  Country
                  <InfoTooltip
                    text="The customer's specific country within the selected region. Used for localized reporting and future regional filtering."
                    position="top"
                  />
                </label>
                <select
                  value={quoteData.country || ''}
                  onChange={(e) => setQuoteData({...quoteData, country: e.target.value})}
                  className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Select Country...</option>
                  {availableCountries.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* ⭐ E13: Currency Selection */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2 flex items-center gap-2">
                  <DollarSign size={14} className="text-green-600" />
                  Currency
                </label>
                <select
                  value={quoteData.currency || 'USD'}
                  onChange={(e) => setQuoteData({...quoteData, currency: e.target.value})}
                  className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  {currencyOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
                <p className="text-xs text-slate-500 mt-1">
                  Auto-selected based on region. Change if needed.
                </p>
              </div>

              {/* ⭐ E12: Quote Validity Period */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2 flex items-center gap-2">
                  <Calendar size={14} className="text-blue-600" />
                  Quote Valid For
                  <InfoTooltip
                    text="How long this quote remains valid after generation. The expiration date appears on the final quote PDF sent to the customer."
                    position="top"
                  />
                </label>
                <select
                  value={quoteData.quoteValidityDays || 30}
                  onChange={(e) => setQuoteData({...quoteData, quoteValidityDays: parseInt(e.target.value)})}
                  className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  {quoteValidityOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
                <p className="text-xs text-slate-500 mt-1">
                  Quote expiration period from date of generation.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* ⭐ M18: Primary/Commercial Contact */}
        <div>
          <h3 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
            <User size={18} className="text-blue-600" />
            Primary Contact
            <span className="text-xs font-normal text-slate-500">(Commercial/Decision Maker)</span>
            <span className="text-xs font-normal text-red-500 bg-red-50 px-2 py-0.5 rounded-full">Required for Quote</span>
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <InputField
              label="Contact Name"
              value={quoteData.contactName}
              onChange={(e) => setQuoteData({...quoteData, contactName: e.target.value})}
              placeholder="Josh Webb"
              autoComplete="off"
              required
            />
            <InputField
              label="Phone"
              value={quoteData.contactPhone}
              onChange={(e) => setQuoteData({...quoteData, contactPhone: e.target.value})}
              placeholder="(703) 225-2179"
              autoComplete="off"
            />
            <InputField
              label="Email"
              value={quoteData.contactEmail}
              onChange={(e) => setQuoteData({...quoteData, contactEmail: e.target.value})}
              placeholder="jwebb@icfconsulting.com"
              autoComplete="new-email"
              required
            />
          </div>
          {(!quoteData.contactName?.trim() || !quoteData.contactEmail?.trim()) && (
            <p className="text-amber-600 text-sm mt-2">Contact name and email are required for generating quotes</p>
          )}
          <div className="mt-2">
            <InputField
              label="Title/Role"
              value={quoteData.contactTitle}
              onChange={(e) => setQuoteData({...quoteData, contactTitle: e.target.value})}
              placeholder="VP of HR Technology"
              autoComplete="off"
            />
          </div>
        </div>

        {/* ⭐ M18: Billing/Contracts Contact */}
        <div>
          <button
            type="button"
            onClick={() => setShowBillingContact(!showBillingContact)}
            className="flex items-center gap-2 text-sm font-medium text-blue-600 hover:text-blue-800 transition-colors"
          >
            {showBillingContact ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            <UserPlus size={16} />
            {showBillingContact ? 'Hide Billing Contact' : 'Add Billing/Contracts Contact'}
          </button>
          
          {showBillingContact && (
            <div className="mt-4 p-4 bg-slate-50 rounded-xl border border-slate-200">
              <h4 className="text-sm font-semibold text-slate-700 mb-4 flex items-center gap-2">
                <User size={16} className="text-green-600" />
                Billing/Contracts Contact
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <InputField
                  label="Name"
                  value={quoteData.billingContactName}
                  onChange={(e) => setQuoteData({...quoteData, billingContactName: e.target.value})}
                  placeholder="Finance contact name"
                  autoComplete="off"
                />
                <InputField
                  label="Phone"
                  value={quoteData.billingContactPhone}
                  onChange={(e) => setQuoteData({...quoteData, billingContactPhone: e.target.value})}
                  placeholder="Phone number"
                  autoComplete="off"
                />
                <InputField
                  label="Email"
                  value={quoteData.billingContactEmail}
                  onChange={(e) => setQuoteData({...quoteData, billingContactEmail: e.target.value})}
                  placeholder="billing@company.com"
                  autoComplete="new-email"
                />
              </div>
            </div>
          )}
        </div>

        {/* ⭐ M18: Influencer/Technical Contact */}
        <div>
          <button
            type="button"
            onClick={() => setShowInfluencerContact(!showInfluencerContact)}
            className="flex items-center gap-2 text-sm font-medium text-blue-600 hover:text-blue-800 transition-colors"
          >
            {showInfluencerContact ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            <UserPlus size={16} />
            {showInfluencerContact ? 'Hide Influencer Contact' : 'Add Influencer/Technical Contact'}
          </button>
          
          {showInfluencerContact && (
            <div className="mt-4 p-4 bg-slate-50 rounded-xl border border-slate-200">
              <h4 className="text-sm font-semibold text-slate-700 mb-4 flex items-center gap-2">
                <User size={16} className="text-purple-600" />
                Influencer/Technical Contact
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <InputField
                  label="Name"
                  value={quoteData.influencerName}
                  onChange={(e) => setQuoteData({...quoteData, influencerName: e.target.value})}
                  placeholder="Technical evaluator name"
                  autoComplete="off"
                />
                <InputField
                  label="Phone"
                  value={quoteData.influencerPhone}
                  onChange={(e) => setQuoteData({...quoteData, influencerPhone: e.target.value})}
                  placeholder="Phone number"
                  autoComplete="off"
                />
                <InputField
                  label="Email"
                  value={quoteData.influencerEmail}
                  onChange={(e) => setQuoteData({...quoteData, influencerEmail: e.target.value})}
                  placeholder="tech@company.com"
                  autoComplete="new-email"
                />
              </div>
              <div className="mt-2">
                <InputField
                  label="Role/Influence"
                  value={quoteData.influencerRole}
                  onChange={(e) => setQuoteData({...quoteData, influencerRole: e.target.value})}
                  placeholder="e.g., IT Director, HRIS Manager"
                  autoComplete="off"
                />
              </div>
            </div>
          )}
        </div>

        {/* Sales Rep Information */}
        <div>
          <h3 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
            <Award size={18} className="text-blue-600" />
            SplashBI Sales Rep
            <span className="text-xs font-normal text-red-500 bg-red-50 px-2 py-0.5 rounded-full">Required for Quote</span>
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <InputField
              label="Sales Rep Name"
              value={quoteData.salesRep}
              onChange={(e) => setQuoteData({...quoteData, salesRep: e.target.value})}
              placeholder="Venkat Ramamurthy"
              required
            />
            <InputField
              label="Sales Rep Email"
              value={quoteData.salesRepEmail}
              onChange={(e) => setQuoteData({...quoteData, salesRepEmail: e.target.value})}
              placeholder="venkat.ramamurthy@splashbi.com"
            />
          </div>
          {!quoteData.salesRep?.trim() && (
            <p className="text-amber-600 text-sm mt-2">Sales rep name is required for generating quotes</p>
          )}
        </div>

        {/* Solution Area / Deal Type */}
        <div>
          <h3 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
            <Package size={18} className="text-blue-600" />
            Solution Area
          </h3>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Deal Type / Solution Area <span className="text-red-500">*</span>
            </label>
            <select
              value={quoteData.dealType}
              onChange={(e) => setQuoteData({...quoteData, dealType: e.target.value})}
              className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">Select Solution Area...</option>
              {dealTypeOptions.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
          {!quoteData.dealType && (
            <p className="text-red-600 text-sm mt-2">⚠️ Solution area is required to proceed</p>
          )}
          <p className="text-xs text-slate-500 mt-2">
            Select the primary solution area for this deal. Choose "Cross-Domain" for deals involving multiple areas.
          </p>
        </div>

        {/* Employee Count - Required for HCM */}
        <div>
          <h3 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
            <Users size={18} className="text-blue-600" />
            Company Size
            {(quoteData.dealType === 'HCM' || quoteData.dealType === 'Cross-Domain') && (
              <span className="text-xs font-normal text-red-600 bg-red-50 px-2 py-1 rounded-full">
                Required for HCM deals
              </span>
            )}
          </h3>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2 flex items-center">
              Number of Employees
              <InfoTooltip
                text="Required for HCM deals to calculate Per Employee Per Month (PEPM) pricing. Used to determine People Analytics costs based on headcount tiers."
                position="right"
              />
              {(quoteData.dealType === 'HCM' || quoteData.dealType === 'Cross-Domain') && (
                <span className="text-red-500"> *</span>
              )}
            </label>
            <input
              type="number"
              min="0"
              value={quoteData.employeeCount || ''}
              onChange={(e) => {
                const value = e.target.value;
                setQuoteData({
                  ...quoteData, 
                  employeeCount: value === '' ? 0 : parseInt(value) || 0
                });
              }}
              placeholder="e.g., 1000"
              className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          {(quoteData.dealType === 'HCM' || quoteData.dealType === 'Cross-Domain') && quoteData.employeeCount === 0 && (
            <p className="text-red-600 text-sm mt-2">⚠️ Employee count is required for HCM deals to proceed</p>
          )}
          <p className="text-xs text-slate-500 mt-2">
            {(quoteData.dealType === 'HCM' || quoteData.dealType === 'Cross-Domain') 
              ? 'Required for calculating Per Employee Per Month (PEPM) metrics for HCM deals'
              : 'Used for calculating Per Employee Per Month (PEPM) metrics when applicable'}
          </p>
        </div>

        </div>
    </div>
  );
};

export default DealInfoStep;
